import React, { useContext, useState } from 'react'
import { AppContext } from '@state/context'
import { openPanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  DIVIDER_SIZE,
  EXAM_DISPLAY_TYPES,
  EXAM_TYPES,
  HEADING_TYPE,
  PANEL_TRANSITION_TYPES,
  PANELS,
  TEXT_SIZES,
  USER_ROLES,
} from '@helpers/constants'
import Divider from '@components/Divider'
import EditExamBlock from '@components/EditExamBlock'
import ExamBlock from '@components/ExamBlock'
import { useConfirm } from '@hooks/useConfirm'
import { useCreateExamAttempt } from '@hooks/mutations/useCreateExamAttempt'
import {
  onCloseExam,
  setCurrentActiveExam,
  setCurrentActiveExamAttemptId,
} from '@state/actions/appActions'
import Panel from '@views/Panels/Panel'
import QuizExamView from '@components/Exams/QuizExamView'
import Heading from '@components/Heading'
import Text from '@components/Text'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import Icon from '@components/Icon'

const Exams = ({ section, editMode }) => {
  const { exams } = section

  const [state, dispatch] = useContext(AppContext)

  const {
    app: { userRole, userId, currentActiveExam },
  } = state

  const { confirm } = useConfirm()

  const { post, isPosting } = useCreateExamAttempt()

  const onStartClick = async (exam) => {
    const content = JSON.parse(exam.content)
    const totalCount = content?.questions?.length

    if (!totalCount) {
      return
    }

    const confirmed = await confirm({
      text: 'Är du säker på att du vill starta testet?',
      additionalText:
        'Om du avbryter ett påbörjat test, eller om du inte får godkänt, kan du göra ett nytt försök imorgon.',
      confirmText: 'Ja, starta test',
      cancelText: 'Nej, starta inte test',
    })

    if (confirmed) {
      if (userRole === USER_ROLES.STUDENT) {
        try {
          dispatch(setCurrentActiveExam(exam))

          await post({
            exam: exam.id,
            customer: userId,
            correctCount: 0,
            incorrectCount: 0,
            totalCount: totalCount,
          })
        } catch (error) {
          console.log('🚀 ~ onStartClick ~ error:', error)
        }
      } else {
        dispatch(setCurrentActiveExam(exam))
      }
    }
  }

  const examsAllowed = useExamsAllowed()

  if (!examsAllowed) {
    return null
  }

  const onlySelfApproval =
    exams &&
    exams.length === 1 &&
    exams[0].type === parseInt(EXAM_TYPES.SELF_APPROVAL.value)

  const introIcon = onlySelfApproval ? 'ExamsExitTicket' : 'ExamsBoard'
  const introHeading = onlySelfApproval ? 'Exit ticket' : 'Test'
  const introText = onlySelfApproval
    ? 'Följ instruktionen nedan. Klicka på <b>Markera som klar</b> för att gå vidare och visa din lärare att du är färdig.'
    : 'Följ instruktionen nedan. Om du inte lyckas med ett test, eller avbryter, så kan du göra ett <b>nytt försök i morgon</b>. Klicka på <b>Starta test</b> för att börja. Lycka till!'

  return (
    <>
      {exams && exams.length > 0 && (
        <div className='max-w-[700px] mx-auto'>
          {/* Exams intro */}
          <Divider size={DIVIDER_SIZE.LARGE} line />
          <Divider size={DIVIDER_SIZE.BASE} />

          <Icon
            icon={introIcon}
            iconClasses='w-[48px] h-[48px]'
            color={COLORS.BLACK.TEXT}
          />
          <Divider size={DIVIDER_SIZE.SMALL} />
          <Heading headingLevel={HEADING_TYPE.H3} size={TEXT_SIZES.XXLARGE}>
            {introHeading}
          </Heading>
          <p
            dangerouslySetInnerHTML={{
              __html: introText,
            }}
          />

          <Divider size={DIVIDER_SIZE.BASE} />

          {exams.map((exam) => {
            const { id } = exam

            return (
              <div key={`exam-${id}`}>
                {editMode ? (
                  <EditExamBlock exam={exam} />
                ) : (
                  <>
                    <ExamBlock
                      exam={exam}
                      onStartClick={() => {
                        onStartClick(exam)
                      }}
                    />
                    <Divider size={DIVIDER_SIZE.SMALL} />
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {editMode && (
        <div className={'flex justify-center mt-8 mb-8'}>
          <Button
            handleClick={() => {
              dispatch(openPanel(PANELS.CREATE_EXAM))
            }}
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            icon={'PlusCircle'}
            iconLeft
            margin
          >
            Skapa nytt test
          </Button>
        </div>
      )}

      {/* Quiz Exam in Panel */}

      {currentActiveExam &&
        currentActiveExam.displayType ===
          parseInt(EXAM_DISPLAY_TYPES.PANEL.value) && (
          <div
            className={
              'absolute top-0 left-0 w-full h-full z-30 bg-black opacity-0'
            }
          />
        )}

      <Panel
        transitionType={PANEL_TRANSITION_TYPES.FROM_LEFT}
        visible={
          currentActiveExam &&
          currentActiveExam.displayType ===
            parseInt(EXAM_DISPLAY_TYPES.PANEL.value)
            ? true
            : false
        }
        width={state.panels.sideBarWidth + 'px'}
        overHeader={true}
      >
        <div className={'flex items-center justify-between'}>
          <div className='text-center flex-1'>
            <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
              {currentActiveExam?.name}
            </Heading>
          </div>

          <div className={'self-start'}>
            <Button
              size={'noPadding'}
              icon={'Close'}
              iconRight
              handleClick={() => {
                dispatch(onCloseExam())
              }}
            />
          </div>
        </div>

        <Divider size={DIVIDER_SIZE.SMALL} line />
        <Divider size={DIVIDER_SIZE.SMALL} />

        <QuizExamView />
      </Panel>
    </>
  )
}

export default Exams
